/**
 * AccessPicker Component
 * Shown at login when a non-admin user holds more than one distinct operational role
 * (e.g. porteiro at one building, síndico at another) — mirrors the mobile app's
 * "choose your access" screen. This picks a ROLE, not a specific customer: once a role
 * is chosen, the existing customer switcher operates within that role's set of
 * customers (e.g. all the buildings where the user is porteiro). A "trocar papel"
 * affordance elsewhere in the app (Layout) can reopen this via openRolePicker().
 */

import React from 'react';
import { Building2, ChevronRight } from 'lucide-react';
import { useKeycloak } from '../contexts/KeycloakContext';
import { useCustomer } from '../contexts/CustomerContext';
import { formatRoleName } from '../config/roles';

const AccessPicker: React.FC = () => {
  const { user } = useKeycloak();
  const { operationalRoles, chooseRole } = useCustomer();

  if (!operationalRoles || operationalRoles.length === 0) {
    return null;
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Escolha seu Acesso</h1>
          <p className="text-gray-600">
            Olá, <span className="font-medium">{user?.firstName || user?.name || 'Usuário'}</span>.
            Sua conta tem mais de um papel — escolha com qual deseja continuar.
          </p>
        </div>

        <div className="space-y-2">
          {operationalRoles.map((role) => (
            <button
              key={role}
              onClick={() => chooseRole(role)}
              className="w-full flex items-center justify-between gap-3 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-lg p-4 text-left transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-gray-500" />
                </div>
                <p className="text-sm font-semibold text-gray-900 truncate">{formatRoleName(role)}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AccessPicker;
